/**
 * Vereinfachter PDF Viewer ohne komplexe Animationen
 */

class SimplePDFViewer {
  constructor(container) {
    this.container = container;
    this.pdfUrl = container.dataset.pdfUrl;
    this.pageMode = container.dataset.pageMode || 'book';
    this.mobilePageMode = container.dataset.mobilePageMode || 'single';
    this.currentPage = 1;
    this.totalPages = 0;
    this.pdf = null;
    this.canvases = [];
    this.bookmarkKey = `pdfviewer_bookmarks_${encodeURIComponent(this.pdfUrl)}`;
    this.bookmarks = [];
    this.isInitialized = false;

    // Touch-Swipe Variablen
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.minSwipeDistance = 50;
    this.maxVerticalDistance = 100;

    this.init();
  }

  getCurrentPageMode() {
    const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Mobil: verwende mobile Einstellung
      return this.mobilePageMode === 'double' ? 'magazine' : 'single';
    } else {
      // Desktop: verwende normale Einstellung  
      return this.pageMode;
    }
  }

  async init() {
    try {
      console.log('Initializing PDF Viewer with URL:', this.pdfUrl);
      if (!this.pdfUrl) {
        throw new Error('Keine PDF-URL angegeben');
      }
      
      console.log('Loading PDF.js...');
      await this.loadPDFJS();
      console.log('PDF.js loaded, loading PDF...');
      await this.loadPDF();
      console.log('PDF loaded, setting up navigation...');
      this.setupNavigation();
      this.isInitialized = true;
      console.log('PDF Viewer initialized successfully');
      
    } catch (error) {
      console.error('PDF Viewer Fehler:', error);
      this.showError(`Fehler: ${error.message}`);
    }
  }

  async loadPDFJS() {
    if (typeof pdfjsLib !== 'undefined') return;

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async loadPDF() {
    const flipbook = this.container.querySelector('.pdfviewer-flipbook');
    const loading = flipbook.querySelector('.pdfviewer-loading');

    console.log('Found flipbook:', flipbook);
    console.log('Found loading element:', loading);

    try {
      console.log('Fetching PDF from:', this.pdfUrl);
      this.pdf = await pdfjsLib.getDocument(this.pdfUrl).promise;
      this.totalPages = this.pdf.numPages;
      console.log('PDF loaded successfully, total pages:', this.totalPages);
    } catch (error) {
      console.error('PDF loading error:', error);
      throw new Error(`PDF konnte nicht geladen werden: ${error.message}`);
    }

    // Container erstellen basierend auf aktuellem Seitenmodus
    const viewer = document.createElement('div');
    viewer.className = 'simple-pdf-viewer';
    const currentMode = this.getCurrentPageMode();
    
    if (currentMode === 'magazine' || currentMode === 'book') {
      // Doppelseiten-Layout für Magazine/Buch-Modus
      viewer.innerHTML = `
        <div class="pdf-spread">
          <canvas id="pdf-canvas-left-${this.container.dataset.sectionId || 'default'}" 
                  class="pdf-page-left" data-side="left"></canvas>
          <canvas id="pdf-canvas-right-${this.container.dataset.sectionId || 'default'}" 
                  class="pdf-page-right" data-side="right"></canvas>
        </div>
      `;
    } else {
      // Einzelseiten-Layout
      viewer.innerHTML = `
        <canvas id="pdf-canvas-${this.container.dataset.sectionId || 'default'}" 
                class="pdf-page-single"></canvas>
      `;
    }

    // Loading-Element entfernen und Viewer hinzufügen
    if (loading) {
      console.log('Removing loading element');
      loading.remove();
    }
    console.log('Adding viewer to flipbook');
    flipbook.appendChild(viewer);

    // Lesezeichen laden falls vorhanden
    console.log('Loading bookmarks');
    this.loadBookmarks();
    this.renderBookmarks();
    
    // Erste Seite(n) rendern
    console.log('Rendering current spread');
    await this.renderCurrentSpread();
    console.log('Updating navigation');
    this.updateNavigation();
  }

  async renderCurrentSpread() {
    const spread = this.container.querySelector('.pdf-spread');
    const singleCanvas = this.container.querySelector('.pdf-page-single');
    const currentMode = this.getCurrentPageMode();
    
    console.log('Rendering spread:', { spread: !!spread, singleCanvas: !!singleCanvas, currentMode });
    
    if (spread) {
      // Doppelseiten-Modus
      const leftCanvas = spread.querySelector('.pdf-page-left');
      const rightCanvas = spread.querySelector('.pdf-page-right');
      
      if (currentMode === 'magazine') {
        await this.renderSinglePage(leftCanvas, this.currentPage);
        if (this.currentPage + 1 <= this.totalPages) {
          await this.renderSinglePage(rightCanvas, this.currentPage + 1);
        } else {
          this.clearCanvas(rightCanvas);
        }
      } else if (currentMode === 'book') {
        if (this.currentPage === 1) {
          await this.renderSinglePage(rightCanvas, 1);
          this.clearCanvas(leftCanvas);
        } else {
          await this.renderSinglePage(leftCanvas, this.currentPage);
          if (this.currentPage + 1 <= this.totalPages) {
            await this.renderSinglePage(rightCanvas, this.currentPage + 1);
          } else {
            this.clearCanvas(rightCanvas);
          }
        }
      }
    } else if (singleCanvas) {
      await this.renderSinglePage(singleCanvas, this.currentPage);
    }
  }

  async renderSinglePage(canvas, pageNum) {
    if (!canvas || pageNum > this.totalPages || pageNum < 1) {
      console.log('Invalid canvas or page number:', { canvas: !!canvas, pageNum, totalPages: this.totalPages });
      return;
    }
    
    console.log('Rendering single page:', pageNum);
    
    try {
      const context = canvas.getContext('2d');
      const page = await this.pdf.getPage(pageNum);
      
      // Container-Abmessungen ermitteln
      const container = canvas.closest('.pdfviewer-flipbook');
      const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const padding = isMobile ? 20 : 40;
      const navHeight = isMobile ? 60 : 80;
      
      let maxWidth = container.offsetWidth - padding;
      let maxHeight = container.offsetHeight - navHeight;
      
      // Bei Doppelseiten die Breite halbieren
      const isSpread = canvas.closest('.pdf-spread');
      if (isSpread) {
        const gap = isMobile ? 10 : 20;
        maxWidth = (maxWidth / 2) - gap;
      }
      
      // Minimum-Abmessungen für mobile Geräte sicherstellen
      if (isMobile) {
        maxWidth = Math.max(maxWidth, 250);
        maxHeight = Math.max(maxHeight, 300);
      }
      
      // Device Pixel Ratio für scharfe Darstellung (auf mobile begrenzen)
      const rawDevicePixelRatio = window.devicePixelRatio || 1;
      const devicePixelRatio = isMobile ? Math.min(rawDevicePixelRatio, 2) : rawDevicePixelRatio;
      
      // Skalierung berechnen
      const viewport = page.getViewport({ scale: 1.0 });
      const scaleX = maxWidth / viewport.width;
      const scaleY = maxHeight / viewport.height;
      const baseScale = Math.min(scaleX, scaleY);
      
      // Angepasste Skalierung für mobile Geräte
      const minScale = isMobile ? 0.5 : 0.8;
      const maxScale = isMobile ? 1.5 : 2.5;
      const scale = Math.max(minScale, Math.min(baseScale, maxScale));
      const scaledViewport = page.getViewport({ scale });
      
      // Debug für mobile Skalierung
      if (isMobile) {
        console.log('Mobile PDF Debug:', {
          mobilePageMode: this.mobilePageMode,
          desktopPageMode: this.pageMode,
          currentMode: this.getCurrentPageMode(),
          containerSize: { width: maxWidth, height: maxHeight },
          pdfSize: { width: viewport.width, height: viewport.height },
          finalScale: scale,
          devicePixelRatio,
          displaySize: { 
            width: scaledViewport.width, 
            height: scaledViewport.height 
          }
        });
      }
      
      // Canvas für High-DPI Displays optimieren
      canvas.width = scaledViewport.width * devicePixelRatio;
      canvas.height = scaledViewport.height * devicePixelRatio;
      canvas.style.width = scaledViewport.width + 'px';
      canvas.style.height = scaledViewport.height + 'px';
      
      // Context für High-DPI skalieren
      context.scale(devicePixelRatio, devicePixelRatio);

      // Canvas leeren
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      await page.render({
        canvasContext: context,
        viewport: scaledViewport
      }).promise;

    } catch (error) {
      console.error('Fehler beim Rendern von Seite', pageNum, ':', error);
    }
  }

  clearCanvas(canvas) {
    if (!canvas) return;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = 0;
    canvas.height = 0;
    canvas.style.width = '0px';
    canvas.style.height = '0px';
  }

  async goToNextSpread() {
    const currentMode = this.getCurrentPageMode();
    
    // Animation für Doppelseiten
    if (currentMode !== 'single') {
      await this.animatePageTurn('next');
    }
    
    if (currentMode === 'single') {
      this.currentPage = Math.min(this.currentPage + 1, this.totalPages);
    } else if (currentMode === 'magazine') {
      this.currentPage = Math.min(this.currentPage + 2, this.totalPages);
    } else if (currentMode === 'book') {
      if (this.currentPage === 1) {
        this.currentPage = 2; // Von Seite 1 zu Doppelseite 2-3
      } else {
        this.currentPage = Math.min(this.currentPage + 2, this.totalPages);
      }
    }
    
    await this.renderCurrentSpread();
  }

  async goToPrevSpread() {
    const currentMode = this.getCurrentPageMode();
    
    // Animation für Doppelseiten
    if (currentMode !== 'single') {
      await this.animatePageTurn('prev');
    }
    
    if (currentMode === 'single') {
      this.currentPage = Math.max(this.currentPage - 1, 1);
    } else if (currentMode === 'magazine') {
      this.currentPage = Math.max(this.currentPage - 2, 1);
    } else if (currentMode === 'book') {
      if (this.currentPage === 2) {
        this.currentPage = 1; // Zurück zur ersten Einzelseite
      } else {
        this.currentPage = Math.max(this.currentPage - 2, 2);
      }
    }
    
    await this.renderCurrentSpread();
  }

  canGoToNext() {
    return this.currentPage < this.totalPages;
  }

  canGoToPrev() {
    return this.currentPage > 1;
  }

  async goToPage(pageNumber) {
    if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) {
      return;
    }

    this.currentPage = pageNumber;
    await this.renderCurrentSpread();
    this.updateNavigation();
    this.updateFullscreenIfActive();
  }

  async animatePageTurn(direction) {
    const spread = this.container.querySelector('.pdf-spread');
    if (!spread) return;

    const rightPage = spread.querySelector('.pdf-page-right');
    const leftPage = spread.querySelector('.pdf-page-left');
    
    if (direction === 'next' && rightPage) {
      // Rechte Seite nach links umblättern (vorwärts)
      rightPage.classList.add('pdf-page-turning-next');
      
      return new Promise(resolve => {
        setTimeout(() => {
          rightPage.classList.remove('pdf-page-turning-next');
          resolve();
        }, 800);
      });
    } else if (direction === 'prev' && leftPage) {
      // Linke Seite nach rechts umblättern (rückwärts)
      leftPage.classList.add('pdf-page-turning-prev');
      
      return new Promise(resolve => {
        setTimeout(() => {
          leftPage.classList.remove('pdf-page-turning-prev');
          resolve();
        }, 800);
      });
    }
  }

  loadBookmarks() {
    const savedBookmarks = localStorage.getItem(this.bookmarkKey);
    if (savedBookmarks) {
      try {
        this.bookmarks = JSON.parse(savedBookmarks);
      } catch (error) {
        console.error('Fehler beim Laden der Lesezeichen:', error);
        this.bookmarks = [];
      }
    }
  }

  saveBookmarks() {
    localStorage.setItem(this.bookmarkKey, JSON.stringify(this.bookmarks));
  }

  addBookmark(page) {
    // Prüfen ob Lesezeichen bereits existiert
    if (!this.bookmarks.find(b => b.page === page)) {
      this.bookmarks.push({
        page: page,
        id: Date.now(),
        createdAt: new Date().toISOString()
      });
      this.saveBookmarks();
      this.renderBookmarks();
    }
  }

  removeBookmark(page) {
    this.bookmarks = this.bookmarks.filter(b => b.page !== page);
    this.saveBookmarks();
    this.renderBookmarks();
  }

  hasBookmark(page = this.currentPage) {
    return this.bookmarks.some(b => b.page === page);
  }

  goToBookmark(page) {
    if (page > 0 && page <= this.totalPages) {
      this.currentPage = page;
      this.renderCurrentSpread();
      this.updateNavigation();
    }
  }

  renderBookmarks() {
    const container = this.container.querySelector('.pdfviewer-bookmarks-container');
    if (!container) return;

    container.innerHTML = '';

    // Lesezeichen nach Seite sortieren
    const sortedBookmarks = [...this.bookmarks].sort((a, b) => a.page - b.page);

    sortedBookmarks.forEach(bookmark => {
      const bookmarkEl = document.createElement('div');
      bookmarkEl.className = 'pdfviewer-bookmark';
      bookmarkEl.dataset.page = bookmark.page;
      bookmarkEl.textContent = bookmark.page;
      bookmarkEl.title = `Zu Seite ${bookmark.page} springen`;

      // Aktives Lesezeichen markieren
      if (bookmark.page === this.currentPage) {
        bookmarkEl.classList.add('pdfviewer-bookmark--active');
      }

      bookmarkEl.addEventListener('click', () => {
        this.goToBookmark(bookmark.page);
      });

      // Rechtsklick zum Löschen
      bookmarkEl.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        this.removeBookmark(bookmark.page);
      });

      container.appendChild(bookmarkEl);
    });
  }

  updateBookmarkIcon() {
    const bookmarkBtn = this.container.querySelector('.pdfviewer-nav-btn--bookmark');
    if (!bookmarkBtn) return;

    const emptyIcon = bookmarkBtn.querySelector('.bookmark-icon-empty');
    const filledIcon = bookmarkBtn.querySelector('.bookmark-icon-filled');
    const hasCurrentBookmark = this.hasBookmark(this.currentPage);

    if (hasCurrentBookmark) {
      emptyIcon.style.display = 'none';
      filledIcon.style.display = 'block';
      bookmarkBtn.title = 'Lesezeichen für diese Seite entfernen';
    } else {
      emptyIcon.style.display = 'block';
      filledIcon.style.display = 'none';
      bookmarkBtn.title = 'Lesezeichen für diese Seite setzen';
    }
  }

  downloadPDF() {
    if (!this.pdfUrl) return;
    
    const link = document.createElement('a');
    link.href = this.pdfUrl;
    link.download = this.pdfUrl.split('/').pop() || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  toggleFullscreen() {
    const fullscreenBtn = this.container.querySelector('[data-action="fullscreen"]');
    const enterIcon = fullscreenBtn?.querySelector('.fullscreen-icon-enter');
    const exitIcon = fullscreenBtn?.querySelector('.fullscreen-icon-exit');
    
    let overlay = document.getElementById('pdfviewer-fullscreen-overlay');
    
    if (overlay && overlay.classList.contains('active')) {
      // Vollbild beenden
      this.exitFullscreen();
      
      // Icon wechseln
      if (enterIcon && exitIcon) {
        enterIcon.style.display = 'block';
        exitIcon.style.display = 'none';
        fullscreenBtn.title = 'Vollbild aktivieren';
      }
    } else {
      // Vollbild aktivieren
      this.enterFullscreen();
      
      // Icon wechseln
      if (enterIcon && exitIcon) {
        enterIcon.style.display = 'none';
        exitIcon.style.display = 'block';
        fullscreenBtn.title = 'Vollbild beenden';
      }
    }
  }

  enterFullscreen() {
    // Erstelle Fullscreen Overlay falls nicht vorhanden
    let overlay = document.getElementById('pdfviewer-fullscreen-overlay');
    if (!overlay) {
      overlay = this.createFullscreenOverlay();
      document.body.appendChild(overlay);
    }

    const fullscreenContent = overlay.querySelector('.pdfviewer-fullscreen-content');
    
    // Vollbild Content erstellen
    fullscreenContent.innerHTML = '';
    
    // Erstelle PDF Viewer Structure
    const flipbookDiv = document.createElement('div');
    flipbookDiv.className = 'pdfviewer-flipbook';
    flipbookDiv.style.width = '100%';
    flipbookDiv.style.height = '100%';
    flipbookDiv.style.background = '#000';
    
    // PDF Viewer Container erstellen
    const viewerDiv = document.createElement('div');
    viewerDiv.className = 'simple-pdf-viewer';
    flipbookDiv.appendChild(viewerDiv);
    
    fullscreenContent.appendChild(flipbookDiv);
    
    // Navigation klonen
    const originalNavigation = this.container.querySelector('.pdfviewer-navigation');
    if (originalNavigation) {
      const clonedNavigation = originalNavigation.cloneNode(true);
      fullscreenContent.appendChild(clonedNavigation);
      
      // Event Listeners für geklonte Navigation hinzufügen
      this.setupFullscreenNavigation(clonedNavigation);
    }
    
    // Vertikaler Slider für Fullscreen klonen und hinzufügen
    const wrapper = this.container.closest('.pdfviewer-wrapper');
    const originalSlider = wrapper?.querySelector('.pdfviewer-vertical-slider');
    if (originalSlider) {
      const clonedSlider = originalSlider.cloneNode(true);
      clonedSlider.classList.add('pdfviewer-fullscreen-slider');
      overlay.appendChild(clonedSlider);
      
      // Event Listeners für geklonten Slider hinzufügen
      this.setupFullscreenSlider(clonedSlider);
    }
    
    // Touch-Events für Fullscreen hinzufügen
    this.addTouchListeners(flipbookDiv);
    
    // Overlay aktivieren
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    // PDF Content rendern
    setTimeout(() => {
      this.renderFullscreenPDF(viewerDiv);
    }, 100);
  }

  exitFullscreen() {
    const overlay = document.getElementById('pdfviewer-fullscreen-overlay');
    if (overlay) {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  createFullscreenOverlay() {
    const overlay = document.createElement('div');
    overlay.id = 'pdfviewer-fullscreen-overlay';
    overlay.className = 'pdfviewer-fullscreen-overlay';
    
    overlay.innerHTML = `
      <button class="pdfviewer-fullscreen-close">×</button>
      <div class="pdfviewer-fullscreen-content"></div>
    `;
    
    // Close Button Event
    const closeBtn = overlay.querySelector('.pdfviewer-fullscreen-close');
    closeBtn.addEventListener('click', () => {
      this.toggleFullscreen();
    });
    
    return overlay;
  }

  setupFullscreenSlider(slider) {
    const verticalSlider = slider.querySelector('.pdfviewer-page-slider-vertical');
    const sliderCurrent = slider.querySelector('.pdfviewer-slider-current');
    const sliderTotal = slider.querySelector('.pdfviewer-slider-total');
    
    if (verticalSlider) {
      verticalSlider.max = this.totalPages;
      verticalSlider.value = this.totalPages - this.currentPage + 1; // Invertiert für top-to-bottom
      
      verticalSlider.addEventListener('input', (e) => {
        const sliderValue = parseInt(e.target.value);
        const targetPage = this.totalPages - sliderValue + 1; // Zurück-invertieren
        this.goToPage(targetPage);
        
        // Fullscreen content aktualisieren
        const fullscreenContent = document.querySelector('.pdfviewer-fullscreen-content');
        const viewerContainer = fullscreenContent?.querySelector('.simple-pdf-viewer');
        if (viewerContainer) {
          this.renderFullscreenSpread(viewerContainer);
        }
        this.updateFullscreenNavigation();
      });
    }
    
    if (sliderCurrent) {
      sliderCurrent.textContent = this.currentPage;
    }
    
    if (sliderTotal) {
      sliderTotal.textContent = this.totalPages;
    }
  }

  setupFullscreenNavigation(navigation) {
    const prevBtn = navigation.querySelector('[data-action="prev"]');
    const nextBtn = navigation.querySelector('[data-action="next"]');
    const bookmarkBtn = navigation.querySelector('[data-action="bookmark"]');
    const downloadBtn = navigation.querySelector('[data-action="download"]');
    const fullscreenBtn = navigation.querySelector('[data-action="fullscreen"]');
    const pageSlider = navigation.querySelector('.pdfviewer-page-slider');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', async () => {
        if (this.canGoToPrev()) {
          await this.goToPrevSpread();
          this.updateFullscreenNavigation();
          // PDF im Fullscreen neu rendern
          const fullscreenContent = document.querySelector('.pdfviewer-fullscreen-content');
          const viewerContainer = fullscreenContent?.querySelector('.simple-pdf-viewer');
          if (viewerContainer) {
            await this.renderFullscreenSpread(viewerContainer);
          }
        }
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', async () => {
        if (this.canGoToNext()) {
          await this.goToNextSpread();
          this.updateFullscreenNavigation();
          // PDF im Fullscreen neu rendern
          const fullscreenContent = document.querySelector('.pdfviewer-fullscreen-content');
          const viewerContainer = fullscreenContent?.querySelector('.simple-pdf-viewer');
          if (viewerContainer) {
            await this.renderFullscreenSpread(viewerContainer);
          }
        }
      });
    }
    
    if (bookmarkBtn) {
      bookmarkBtn.addEventListener('click', () => {
        if (this.hasBookmark(this.currentPage)) {
          this.removeBookmark(this.currentPage);
        } else {
          this.addBookmark(this.currentPage);
        }
        this.updateFullscreenBookmarkIcon();
      });
    }
    
    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => {
        this.downloadPDF();
      });
    }
    
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', () => {
        this.toggleFullscreen();
      });
    }

    // Page Slider für Fullscreen Setup
    if (pageSlider) {
      pageSlider.max = this.totalPages;
      pageSlider.value = this.currentPage;
      
      pageSlider.addEventListener('input', (e) => {
        const targetPage = parseInt(e.target.value);
        this.goToPage(targetPage);
      });
    }
  }

  updateFullscreenNavigation() {
    const overlay = document.getElementById('pdfviewer-fullscreen-overlay');
    if (!overlay) return;
    
    const navigation = overlay.querySelector('.pdfviewer-navigation');
    if (!navigation) return;
    
    const prevBtn = navigation.querySelector('[data-action="prev"]');
    const nextBtn = navigation.querySelector('[data-action="next"]');
    const currentPageSpan = navigation.querySelector('.pdfviewer-current-page');
    const pageSlider = navigation.querySelector('.pdfviewer-page-slider');
    
    // Vertikaler Slider auch aktualisieren
    const slider = overlay.querySelector('.pdfviewer-fullscreen-slider');
    if (slider) {
      const verticalSlider = slider.querySelector('.pdfviewer-page-slider-vertical');
      const sliderCurrent = slider.querySelector('.pdfviewer-slider-current');
      
      if (verticalSlider) {
        verticalSlider.value = this.totalPages - this.currentPage + 1; // Invertiert
      }
      
      if (sliderCurrent) {
        sliderCurrent.textContent = this.currentPage;
      }
    }
    
    if (prevBtn) {
      prevBtn.disabled = this.currentPage === 1;
    }
    
    if (nextBtn) {
      nextBtn.disabled = this.currentPage === this.totalPages;
    }
    
    if (currentPageSpan) {
      const currentMode = this.getCurrentPageMode();
      let displayText = this.currentPage;
      
      if (currentMode === 'book' && this.currentPage > 1 && this.currentPage < this.totalPages) {
        // Im Buchmodus bei Doppelseiten beide Seiten anzeigen
        displayText = `${this.currentPage}-${Math.min(this.currentPage + 1, this.totalPages)}`;
      } else if (currentMode === 'magazine' && this.currentPage < this.totalPages) {
        // Im Magazin-Modus immer Doppelseiten anzeigen
        displayText = `${this.currentPage}-${Math.min(this.currentPage + 1, this.totalPages)}`;
      }
      
      currentPageSpan.textContent = displayText;
    }

    if (pageSlider) {
      pageSlider.value = this.currentPage;
    }
  }

  updateFullscreenBookmarkIcon() {
    const overlay = document.getElementById('pdfviewer-fullscreen-overlay');
    if (!overlay) return;
    
    const bookmarkBtn = overlay.querySelector('.pdfviewer-nav-btn--bookmark');
    if (!bookmarkBtn) return;
    
    const emptyIcon = bookmarkBtn.querySelector('.bookmark-icon-empty');
    const filledIcon = bookmarkBtn.querySelector('.bookmark-icon-filled');
    const hasCurrentBookmark = this.hasBookmark(this.currentPage);
    
    if (hasCurrentBookmark) {
      emptyIcon.style.display = 'none';
      filledIcon.style.display = 'block';
    } else {
      emptyIcon.style.display = 'block';
      filledIcon.style.display = 'none';
    }
  }

  async renderFullscreenPDF(viewerContainer) {
    if (!this.pdf) {
      console.error('PDF not loaded for fullscreen');
      return;
    }
    
    const currentMode = this.getCurrentPageMode();
    
    if (currentMode === 'magazine' || currentMode === 'book') {
      // Doppelseiten-Layout für Magazine/Buch-Modus
      viewerContainer.innerHTML = `
        <div class="pdf-spread">
          <canvas id="pdf-canvas-left-fullscreen" class="pdf-page-left" data-side="left"></canvas>
          <canvas id="pdf-canvas-right-fullscreen" class="pdf-page-right" data-side="right"></canvas>
        </div>
      `;
    } else {
      // Einzelseiten-Layout
      viewerContainer.innerHTML = `
        <canvas id="pdf-canvas-fullscreen" class="pdf-page-single"></canvas>
      `;
    }
    
    // Seiten rendern
    await this.renderFullscreenSpread(viewerContainer);
    
    // Navigation aktualisieren
    this.updateFullscreenNavigation();
  }
  
  async renderFullscreenSpread(viewerContainer) {
    const spread = viewerContainer.querySelector('.pdf-spread');
    const singleCanvas = viewerContainer.querySelector('.pdf-page-single');
    const currentMode = this.getCurrentPageMode();
    
    if (spread) {
      // Doppelseiten-Modus
      const leftCanvas = spread.querySelector('.pdf-page-left');
      const rightCanvas = spread.querySelector('.pdf-page-right');
      
      if (currentMode === 'magazine') {
        await this.renderSinglePage(leftCanvas, this.currentPage);
        if (this.currentPage + 1 <= this.totalPages) {
          await this.renderSinglePage(rightCanvas, this.currentPage + 1);
        } else {
          this.clearCanvas(rightCanvas);
        }
      } else if (currentMode === 'book') {
        if (this.currentPage === 1) {
          await this.renderSinglePage(rightCanvas, 1);
          this.clearCanvas(leftCanvas);
        } else {
          await this.renderSinglePage(leftCanvas, this.currentPage);
          if (this.currentPage + 1 <= this.totalPages) {
            await this.renderSinglePage(rightCanvas, this.currentPage + 1);
          } else {
            this.clearCanvas(rightCanvas);
          }
        }
      }
    } else if (singleCanvas) {
      await this.renderSinglePage(singleCanvas, this.currentPage);
    }
  }
  
  async renderFullscreenContent(fullscreenContent) {
    // Diese Methode wird nicht mehr verwendet, aber behalten für Kompatibilität
    const viewerContainer = fullscreenContent.querySelector('.simple-pdf-viewer');
    if (viewerContainer) {
      await this.renderFullscreenPDF(viewerContainer);
    }
  }

  setupNavigation() {
    // Navigation ist jetzt außerhalb des Containers, im Wrapper
    const wrapper = this.container.closest('.pdfviewer-wrapper') || this.container.parentElement;
    const navigation = wrapper?.querySelector('.pdfviewer-navigation') || this.container.querySelector('.pdfviewer-navigation');
    if (!navigation) {
      console.error('Navigation not found');
      return;
    }

    const prevBtn = navigation.querySelector('[data-action="prev"]');
    const nextBtn = navigation.querySelector('[data-action="next"]');
    const bookmarkBtn = navigation.querySelector('[data-action="bookmark"]');
    const downloadBtn = navigation.querySelector('[data-action="download"]');
    const fullscreenBtn = navigation.querySelector('[data-action="fullscreen"]');
    const currentPageSpan = navigation.querySelector('.pdfviewer-current-page');
    const totalPagesSpan = navigation.querySelector('.pdfviewer-total-pages');
    
    // Vertikaler Slider (außerhalb der Navigation, aber im selben Container-Bereich)
    const verticalSlider = wrapper?.querySelector('.pdfviewer-page-slider-vertical');
    const sliderCurrent = wrapper?.querySelector('.pdfviewer-slider-current');
    const sliderTotal = wrapper?.querySelector('.pdfviewer-slider-total');

    if (totalPagesSpan) {
      totalPagesSpan.textContent = this.totalPages;
    }

    if (sliderTotal) {
      sliderTotal.textContent = this.totalPages;
    }

    // Vertikaler Slider Setup
    if (verticalSlider) {
      verticalSlider.max = this.totalPages;
      verticalSlider.value = this.totalPages - this.currentPage + 1; // Invertiert für top-to-bottom
      
      verticalSlider.addEventListener('input', (e) => {
        const sliderValue = parseInt(e.target.value);
        const targetPage = this.totalPages - sliderValue + 1; // Zurück-invertieren
        this.goToPage(targetPage);
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', async () => {
        console.log('Previous button clicked');
        if (this.canGoToPrev()) {
          prevBtn.disabled = true;
          await this.goToPrevSpread();
          this.updateNavigation();
          prevBtn.disabled = false;
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', async () => {
        console.log('Next button clicked');
        if (this.canGoToNext()) {
          nextBtn.disabled = true;
          await this.goToNextSpread();
          this.updateNavigation();
          nextBtn.disabled = false;
        }
      });
    }

    // Lesezeichen-Button
    if (bookmarkBtn) {
      bookmarkBtn.addEventListener('click', () => {
        if (this.hasBookmark(this.currentPage)) {
          this.removeBookmark(this.currentPage);
        } else {
          this.addBookmark(this.currentPage);
        }
        this.updateBookmarkIcon();
      });
    }

    // Download-Button
    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => {
        this.downloadPDF();
      });
    }

    // Fullscreen-Button
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', () => {
        console.log('Fullscreen button clicked');
        this.toggleFullscreen();
      });
    }

    // Keyboard navigation
    document.addEventListener('keydown', async (e) => {
      if (!this.isInitialized) return;
      
      if (e.key === 'ArrowLeft' && this.canGoToPrev()) {
        e.preventDefault();
        await this.goToPrevSpread();
        this.updateNavigation();
      } else if (e.key === 'ArrowRight' && this.canGoToNext()) {
        e.preventDefault();
        await this.goToNextSpread();
        this.updateNavigation();
      } else if (e.key === 'b' && bookmarkBtn) {
        e.preventDefault();
        if (this.hasBookmark(this.currentPage)) {
          this.removeBookmark(this.currentPage);
        } else {
          this.addBookmark(this.currentPage);
        }
        this.updateBookmarkIcon();
      } else if (e.key === 'Escape') {
        // ESC beendet Vollbild-Modus
        const overlay = document.getElementById('pdfviewer-fullscreen-overlay');
        if (overlay && overlay.classList.contains('active')) {
          e.preventDefault();
          this.toggleFullscreen();
        }
      }
    });

    // Lesezeichen-Icon initial aktualisieren
    this.updateBookmarkIcon();

    // Touch-Swipe Navigation hinzufügen
    this.setupTouchNavigation();

    // Click navigation
    const canvas = this.container.querySelector('canvas');
    if (canvas) {
      canvas.addEventListener('click', async (e) => {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const centerX = rect.width / 2;

        if (x > centerX && this.currentPage < this.totalPages) {
          await this.renderPage(this.currentPage + 1);
          this.updateNavigation();
        } else if (x <= centerX && this.currentPage > 1) {
          await this.renderPage(this.currentPage - 1);
          this.updateNavigation();
        }
      });
    }
  }

  updateNavigation() {
    const wrapper = this.container.closest('.pdfviewer-wrapper') || this.container.parentElement;
    const navigation = wrapper?.querySelector('.pdfviewer-navigation') || this.container.querySelector('.pdfviewer-navigation');
    if (!navigation) return;

    const prevBtn = navigation.querySelector('[data-action="prev"]');
    const nextBtn = navigation.querySelector('[data-action="next"]');
    const currentPageSpan = navigation.querySelector('.pdfviewer-current-page');
    
    // Vertikaler Slider
    const verticalSlider = wrapper?.querySelector('.pdfviewer-page-slider-vertical');
    const sliderCurrent = wrapper?.querySelector('.pdfviewer-slider-current');

    if (prevBtn) {
      prevBtn.disabled = this.currentPage === 1;
    }

    if (nextBtn) {
      nextBtn.disabled = this.currentPage === this.totalPages;
    }

    if (currentPageSpan) {
      const currentMode = this.getCurrentPageMode();
      let displayText = this.currentPage;
      
      if (currentMode === 'book' && this.currentPage > 1 && this.currentPage < this.totalPages) {
        // Im Buchmodus bei Doppelseiten beide Seiten anzeigen
        displayText = `${this.currentPage}-${Math.min(this.currentPage + 1, this.totalPages)}`;
      } else if (currentMode === 'magazine' && this.currentPage < this.totalPages) {
        // Im Magazin-Modus immer Doppelseiten anzeigen
        displayText = `${this.currentPage}-${Math.min(this.currentPage + 1, this.totalPages)}`;
      }
      
      currentPageSpan.textContent = displayText;
    }

    if (verticalSlider) {
      verticalSlider.value = this.totalPages - this.currentPage + 1; // Invertiert
    }

    if (sliderCurrent) {
      sliderCurrent.textContent = this.currentPage;
    }

    // Lesezeichen-Icon und visuelle Lesezeichen aktualisieren
    this.updateBookmarkIcon();
    this.renderBookmarks();
  }

  setupTouchNavigation() {
    // Touch-Events für normale Ansicht
    const flipbook = this.container.querySelector('.pdfviewer-flipbook');
    if (flipbook) {
      this.addTouchListeners(flipbook);
      console.log('Touch events added to flipbook');
    }
  }

  addTouchListeners(element) {
    element.addEventListener('touchstart', (e) => {
      this.handleTouchStart(e);
    }, { passive: false });

    element.addEventListener('touchmove', (e) => {
      this.handleTouchMove(e);
    }, { passive: false });

    element.addEventListener('touchend', (e) => {
      this.handleTouchEnd(e);
    }, { passive: false });
  }

  handleTouchStart(e) {
    const firstTouch = e.touches[0];
    this.touchStartX = firstTouch.clientX;
    this.touchStartY = firstTouch.clientY;
  }

  handleTouchMove(e) {
    if (!this.touchStartX || !this.touchStartY) {
      return;
    }

    const firstTouch = e.touches[0];
    this.touchEndX = firstTouch.clientX;
    this.touchEndY = firstTouch.clientY;

    // Verhindere Standardverhalten nur bei horizontalen Swipes
    const deltaX = Math.abs(this.touchEndX - this.touchStartX);
    const deltaY = Math.abs(this.touchEndY - this.touchStartY);
    
    if (deltaX > deltaY && deltaX > 20) {
      e.preventDefault();
    }
  }

  async handleTouchEnd(e) {
    if (!this.touchStartX || !this.touchStartY) {
      return;
    }

    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = Math.abs(this.touchEndY - this.touchStartY);

    console.log('Touch end:', { deltaX, deltaY, minSwipe: this.minSwipeDistance });

    // Prüfe ob es ein gültiger horizontaler Swipe ist
    if (Math.abs(deltaX) >= this.minSwipeDistance && deltaY <= this.maxVerticalDistance) {
      console.log('Valid swipe detected');
      if (deltaX > 0) {
        // Swipe nach rechts - vorherige Seite
        console.log('Swipe right - previous page');
        if (this.canGoToPrev()) {
          await this.goToPrevSpread();
          this.updateNavigation();
          this.updateFullscreenIfActive();
        }
      } else {
        // Swipe nach links - nächste Seite
        console.log('Swipe left - next page');
        if (this.canGoToNext()) {
          await this.goToNextSpread();
          this.updateNavigation();
          this.updateFullscreenIfActive();
        }
      }
    }

    // Reset touch values
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
  }

  updateFullscreenIfActive() {
    // Update Fullscreen falls aktiv
    const overlay = document.getElementById('pdfviewer-fullscreen-overlay');
    if (overlay && overlay.classList.contains('active')) {
      const fullscreenContent = overlay.querySelector('.pdfviewer-fullscreen-content');
      const viewerContainer = fullscreenContent?.querySelector('.simple-pdf-viewer');
      if (viewerContainer) {
        this.renderFullscreenSpread(viewerContainer);
      }
      this.updateFullscreenNavigation();
    }
  }

  showError(message) {
    console.error('Showing error:', message);
    const flipbook = this.container.querySelector('.pdfviewer-flipbook');
    if (flipbook) {
      flipbook.innerHTML = `<div class="pdfviewer-error"><p>${message}</p></div>`;
    } else {
      console.error('Could not find flipbook element to show error');
    }
  }
}

// Auto-init
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM Content Loaded, looking for PDF viewers...');
  const containers = document.querySelectorAll('.pdfviewer-container[data-pdf-url]');
  console.log('Found PDF viewer containers:', containers.length);
  containers.forEach((container, index) => {
    console.log(`Initializing PDF viewer ${index + 1}:`, container.dataset.pdfUrl);
    new SimplePDFViewer(container);
  });
});

window.SimplePDFViewer = SimplePDFViewer;